import { Suspense, lazy } from "react";
import { Navigate, Route, Routes, useSearchParams } from "react-router-dom";
import Layout from "./components/Layout.js";
import { Spinner } from "./components/ui.js";
import Home from "./pages/Home.js";
import Season from "./pages/Season.js";
import News from "./pages/News.js";
import Article from "./pages/Article.js";
import Managers from "./pages/Managers.js";
import Rules from "./pages/Rules.js";
import Login from "./pages/Login.js";
import GameDetail from "./pages/GameDetail.js";

// The heavy read-only pages are as worth splitting out as the signed-in ones:
// between them they pull in the whole chart module, which the home page and
// the news archive never touch.
const ManagerProfile = lazy(() => import("./pages/ManagerProfile.js"));
const Leaders = lazy(() => import("./pages/Leaders.js"));
const Teams = lazy(() => import("./pages/Teams.js"));
const ChassisDetail = lazy(() => import("./pages/ChassisDetail.js"));
// Reached by URL only while the league tries it out; no nav entry yet.
const Matchup = lazy(() => import("./pages/Matchup.js"));

// Only reachable when signed in, so they don't need to be in the bundle
// everyone downloads.
const SubmitQueue = lazy(() => import("./pages/SubmitQueue.js"));
const SubmitResult = lazy(() => import("./pages/SubmitResult.js"));
const Admin = lazy(() => import("./pages/Admin.js"));
const ArticleEditor = lazy(() => import("./pages/ArticleEditor.js"));
const Account = lazy(() => import("./pages/Account.js"));

/**
 * Send an old URL to its replacement, keeping the query string.
 *
 * `/standings` and `/schedule` became views of one Season page and `/board` a
 * view of Teams. Links to the old paths exist in Discord and in the league's
 * own articles, so they redirect rather than 404.
 */
/**
 * The head-to-head page folded into the manager profile, where the opponent
 * list already lived, and is now one chip in that page's split filter. `?a=`
 * becomes the profile and `?b=` the opponent it is filtered to, so every old
 * link and bookmark still lands on the same series.
 */
function MovedToProfile() {
  const [params] = useSearchParams();
  const a = params.get("a");
  const b = params.get("b");
  if (!a) return <Navigate to="/managers" replace />;
  const qs = b ? `?${new URLSearchParams({ split: `vs-${b}` })}` : "";
  return <Navigate to={`/managers/${a}${qs}`} replace />;
}

function Moved({ to, view }: { to: string; view?: string }) {
  const [params] = useSearchParams();
  const next = new URLSearchParams(params);
  if (view) next.set("view", view);
  const qs = next.toString();
  return <Navigate to={qs ? `${to}?${qs}` : to} replace />;
}

export default function App() {
  return (
    <Layout>
      <Suspense fallback={<Spinner />}>
        <Routes>
          <Route path="/" element={<Home />} />

          <Route path="/season" element={<Season />} />
          <Route path="/standings" element={<Moved to="/season" />} />
          <Route path="/schedule" element={<Moved to="/season" view="schedule" />} />

          <Route path="/games/:id" element={<GameDetail />} />
          <Route path="/news" element={<News />} />
          <Route path="/news/:slug" element={<Article />} />
          <Route path="/managers" element={<Managers />} />
          <Route path="/managers/:slug" element={<ManagerProfile />} />
          <Route path="/leaders" element={<Leaders />} />
          <Route path="/head-to-head" element={<MovedToProfile />} />

          <Route path="/teams" element={<Teams />} />
          <Route path="/teams/:abbr" element={<ChassisDetail />} />
          <Route path="/matchup" element={<Matchup />} />
          <Route path="/board" element={<Moved to="/teams" view="board" />} />

          <Route path="/rules" element={<Rules />} />
          <Route path="/login" element={<Login />} />
          <Route path="/account" element={<Account />} />
          <Route path="/submit" element={<SubmitQueue />} />
          <Route path="/submit/:gameId" element={<SubmitResult />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/articles/new" element={<ArticleEditor />} />
          <Route path="/admin/articles/:slug" element={<ArticleEditor />} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
    </Layout>
  );
}
